import React from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';

import { DuplicateEmployee, DuplicateJobApplication } from '../jobApplicationApi';

const statusNames: Record<string, string> = {
  called_for_interview: 'Kallad',
  D_reserve: 'Reserv (D)',
  TL_reserve: 'Reserv (TL)',
  employed: 'Anställd',
};

type Props = {
  employees: DuplicateEmployee[];
  jobApplications: DuplicateJobApplication[];
  selectedId?: number;
  onSelect: (id: number) => void;
  onCancel: () => void;
  onUpdate: () => void;
  onCreate: () => void;
  submitting: boolean;
};

// What an employee has in common with the application
const matchDescription = (employee: DuplicateEmployee): string =>
  [employee.same_name && 'samma namn', employee.same_contact && 'samma e-post eller mobilnummer']
    .filter(Boolean)
    .join(', ');

const EmployeeLinks: React.FC<{ employees: DuplicateEmployee[]; showMatch?: boolean }> = ({ employees, showMatch }) => (
  <ul className="mt-2 list-disc pl-6">
    {employees.map((employee) => (
      <li key={employee.id}>
        <Link to={`/users/${employee.id}`} target="_blank" rel="noreferrer" className="underline">
          {employee.name}
        </Link>
        {showMatch && <span> ({matchDescription(employee)})</span>}
      </li>
    ))}
  </ul>
);

// Shown before staff create an application: blocks it for a D or TL employee that is the same person. Otherwise it
// warns about employees with the same name, mobile or email, and lets them update one of the applications with the
// same mobile or email, or create a new one anyway.
const DuplicateJobApplicationsDialog: React.FC<Props> = (props) => {
  const blockingEmployees = props.employees.filter((employee) => employee.blocking);
  const otherEmployees = props.employees.filter((employee) => !employee.blocking);

  if (blockingEmployees.length > 0) {
    return (
      <Dialog open onClose={props.onCancel} fullWidth maxWidth="sm">
        <DialogTitle>Personen är redan anställd</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Det finns en aktiv deltagare eller teamledare med samma uppgifter, så ansökan kan inte skapas.
          </DialogContentText>
          <EmployeeLinks employees={blockingEmployees} />
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onCancel} variant="contained" color="primary">
            Stäng
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={props.onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Kontrollera innan ansökan skapas</DialogTitle>
      <DialogContent>
        {otherEmployees.length > 0 && (
          <div className="mb-4">
            <DialogContentText>
              Det finns aktiva deltagare eller teamledare med samma namn, e-post eller mobilnummer. Kontrollera att det
              inte är samma person:
            </DialogContentText>
            <EmployeeLinks employees={otherEmployees} showMatch />
          </div>
        )}
        {props.jobApplications.length > 0 && (
          <DialogContentText>
            Denna ansökan liknar följande som redan finns i systemet. Om du är säker på att det berör samma person, välj
            att uppdatera den befintliga ansökan. Är du osäker, skapa en ny.
          </DialogContentText>
        )}
        <RadioGroup
          className="mt-4"
          value={props.selectedId ?? ''}
          onChange={(event): void => props.onSelect(Number(event.target.value))}
        >
          {props.jobApplications.map((jobApplication) => (
            <FormControlLabel
              key={jobApplication.id}
              value={jobApplication.id}
              control={<Radio />}
              label={
                <span>
                  <span className="font-semibold">
                    {jobApplication.first_name} {jobApplication.surname}
                  </span>
                  {jobApplication.same_person && <span className="italic"> (samma person)</span>}
                  {[
                    jobApplication.email,
                    jobApplication.mobile,
                    jobApplication.birth_date,
                    statusNames[jobApplication.status],
                    `skapad ${jobApplication.created_at.slice(0, 10)}`,
                  ]
                    .filter(Boolean)
                    .map((detail) => ` · ${detail}`)}
                </span>
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel} color="error" variant="contained" disabled={props.submitting}>
          Avbryt
        </Button>
        {props.jobApplications.length > 0 && (
          <Button
            onClick={props.onUpdate}
            color="primary"
            variant="contained"
            disabled={props.submitting || props.selectedId === undefined}
          >
            Uppdatera
          </Button>
        )}
        <Button onClick={props.onCreate} color="primary" variant="outlined" disabled={props.submitting}>
          Skapa ny
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateJobApplicationsDialog;
